import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PlaylistItem } from '@jwp/ott-common/types/playlist';
import ArrowLeft from '@jwp/ott-theme/assets/icons/arrow_left.svg?react';
import Replay from '@jwp/ott-theme/assets/icons/replay.svg?react';

import IconButton from '../../components/IconButton/IconButton';
import PlayerContainer from '../PlayerContainer/PlayerContainer';
import Fade from '../../components/Animation/Fade/Fade';
import Icon from '../../components/Icon/Icon';
import Modal from '../../components/Modal/Modal';
import Button from '../../components/Button/Button';

import styles from './Cinema.module.scss';

type ContextualAction = 'START_FROM_BEGINNING'; // 'SKIP_INTRO' | 'NEXT_EPISODE' | ...

type Props = {
  open: boolean;
  item: PlaylistItem;
  seriesItem?: PlaylistItem;
  contextualActions?: ContextualAction[];
  onContextualActionClick?: (action: ContextualAction) => void;
  onPlay?: () => void;
  onPause?: () => void;
  onComplete?: () => void;
  onClose?: () => void;
  onNext?: () => void;
  feedId?: string;
  title: string;
  primaryMetadata: React.ReactNode;
  secondaryMetadata?: React.ReactNode;
  liveStartDateTime?: string | null;
  liveEndDateTime?: string | null;
  liveFromBeginning?: boolean;
};

const Cinema: React.FC<Props> = ({
  open,
  item,
  seriesItem,
  title,
  primaryMetadata,
  secondaryMetadata,
  contextualActions = [],
  onContextualActionClick,
  onPlay,
  onPause,
  onComplete,
  onClose,
  onNext,
  feedId,
  liveStartDateTime,
  liveEndDateTime,
  liveFromBeginning,
}: Props) => {
  const { t } = useTranslation();

  // state
  const [isPlaying, setIsPlaying] = useState(false);
  const [overlayHasFocus, setOverlayHasFocus] = useState(false);
  const [userActive, setUserActive] = useState(true);

  const handlePlay = useCallback(() => {
    setIsPlaying(true);
    onPlay?.();
  }, [onPlay]);

  const handlePause = useCallback(() => {
    setIsPlaying(false);
    onPause?.();
  }, [onPause]);

  const handleComplete = useCallback(() => {
    onComplete?.();
  }, [onComplete]);

  const handleNext = useCallback(() => {
    onNext?.();
  }, [onNext]);

  const handleUserActive = useCallback(() => setUserActive(true), []);
  const handleUserInactive = useCallback(() => setUserActive(false), []);

  // effects
  useEffect(() => {
    if (open) setUserActive(true);
  }, [open]);

  // @CUSTOMER vd-ott-iq contextual action element has been added
  const contextualActionsElement = useMemo(() => {
    const elements = contextualActions
      ?.map((action) => {
        if (action === 'START_FROM_BEGINNING') {
          return (
            <Button
              key={action}
              onClick={() => onContextualActionClick?.(action)}
              className={styles.contextualActionLeft}
              label={t('epg:start_from_beginning')}
              variant="contained"
              color="default"
              startIcon={<Replay />}
            />
          );
        }
        return null;
      })
      .filter((element) => !!element);

    if (!elements.length) return null;

    return <div className={styles.contextualActions}>{elements}</div>;
  }, [contextualActions, onContextualActionClick, t]);

  const overlayVisible = !isPlaying || userActive || overlayHasFocus;

  return (
    <Modal open={open} animationContainerClassName={styles.cinemaContainer} onClose={onClose} aria-label={t('videoplayer')} isFullScreen>
      <div className={styles.cinema}>
        <Fade className={styles.overlayFade} open={overlayVisible} keepMounted>
          <div
            className={styles.playerOverlay}
            onMouseEnter={() => setOverlayHasFocus(true)}
            onMouseLeave={() => setOverlayHasFocus(false)}
            onFocus={() => setOverlayHasFocus(true)}
            onBlur={() => setOverlayHasFocus(false)}
          >
            <div className={styles.playerContent}>
              <IconButton aria-label={t('common:back')} onClick={onClose} className={styles.backButton}>
                <Icon icon={ArrowLeft} />
              </IconButton>
              <div>
                <h1 className={styles.title}>{title}</h1>
                <div className={styles.metaContainer}>
                  {secondaryMetadata && <div className={styles.secondaryMetadata}>{secondaryMetadata}</div>}
                  <div className={styles.primaryMetadata}>{primaryMetadata}</div>
                </div>
              </div>
            </div>
          </div>
        </Fade>
        {contextualActionsElement ? (
          <Fade className={styles.contextualActionsOverlay} open={overlayVisible} keepMounted>
            {contextualActionsElement}
          </Fade>
        ) : null}
        <PlayerContainer
          item={item}
          seriesItem={seriesItem}
          feedId={feedId}
          autostart={true}
          onPlay={handlePlay}
          onPause={handlePause}
          onComplete={handleComplete}
          onUserActive={handleUserActive}
          onUserInActive={handleUserInactive}
          onNext={handleNext}
          onBackClick={onClose}
          liveEndDateTime={liveEndDateTime}
          liveFromBeginning={liveFromBeginning}
          liveStartDateTime={liveStartDateTime}
        />
      </div>
    </Modal>
  );
};

export default Cinema;
